import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { getUserInfo } from './apiUtils.js';
import { ChevronDown, Search, SortAsc, SortDesc, Calendar, Filter, X, Plus, Edit, Save, XCircle, Check } from 'lucide-react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';



const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyRoles = message.startsWith('No User Roles found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptyRoles ? '16px' : '8px',
        border: isEmptyRoles ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptyRoles ? '16.8px' : '18px',
          lineHeight: isEmptyRoles ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptyRoles ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyRoles ? '120px' : '100%',
              margin: isEmptyRoles ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const UserRolesDefinition = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [redirectPath, setRedirectPath] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [permissions, setPermissions] = useState({
    canUpdate: false,
    canViewAccessLevelDefinitions: false,
  });

  const login_token = localStorage.getItem('authToken');
  const [roles, setRoles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [corporateAccount, setCorporateAccount] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');

  // State for inline editing
  const [editingRows, setEditingRows] = useState(new Set());
  const [editValues, setEditValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // State for pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to roles
  const sortedRoles = React.useMemo(() => {
    let sortableRoles = [...filteredRoles];
    if (sortConfig.key) {
      sortableRoles.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Special handling for level_name to sort numerically
        if (sortConfig.key === 'level_name') {
          aValue = parseInt(aValue.replace('level_', ''));
          bValue = parseInt(bValue.replace('level_', ''));
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableRoles;
  }, [filteredRoles, sortConfig]);

  const styles = {
    editButton: {
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    saveButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '4px 8px',
      height: '24px',
      fontSize: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '4px'
    },
    cancelButton: {
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '4px 8px',
      height: '24px',
      fontSize: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6
    },
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
    },
    th: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px',
      cursor: 'pointer',
      userSelect: 'none',
      position: 'relative'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },
    editInput: {
      width: '100%',
      padding: '4px 8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '0.875rem'
    },
    button: {
      padding: '6px 12px',
      margin: '0 5px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    error: {
      color: 'red',
      padding: '10px',
      marginBottom: '10px',
    },
  };

  const checkPermission = async (categoryHeader, categorySubHeaders) => {
    console.log(`Checking permissions for ${categoryHeader} with sub-headers:`, categorySubHeaders);

    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          category_header: categoryHeader,
          category_sub_header: categorySubHeaders || []
        }),
      });
      const data = await response.json();
      console.log(`API response for ${categoryHeader}:`, data);

      // Create new permissions object
      const newPermissions = {};

      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
        console.log(`Processing user action: ${item.user_action}, has_access: ${item.has_access}`);

        switch(item.user_action) {
          case 'Edit Access Level Definitions':
            newPermissions.canUpdate = item.has_access;
            break;
          case 'View Access Level Definitions':
            newPermissions.canViewAccessLevelDefinitions = item.has_access;
            console.log(`User has permission to view access level definitions: ${item.has_access}`);
            break;
        }
      });

      setPermissions(prevPermissions => ({
        ...prevPermissions,
        ...newPermissions
      }));

      // Return the new permissions so they can be used immediately
      return {
        success: data.access_status && data.status === 'Success',
        permissions: newPermissions
      };
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return {
        success: false,
        permissions: {}
      };
    }
  };

  useEffect(() => {
    if (corporateAccount && userId) {
      // Check permissions first, then fetch roles only if user has view permission
      const initializeComponent = async () => {
        try {
          console.log('Checking permissions for User Roles Definition...');

          const permissionResult = await checkPermission('Admin Functions', ['Edit Access Level Definitions', 'View Access Level Definitions']);

          console.log('Permission check result:', permissionResult);

          // Only fetch roles if user has view permission
          if (permissionResult.permissions?.canViewAccessLevelDefinitions) {
            console.log('User has view permission, fetching roles...');
            await fetchRoles();
          } else {
            console.log('User does not have permission to view access level definitions');
            setRoles([]);
          }
        } catch (error) {
          console.error('Error initializing component:', error);
        }
      };

      initializeComponent();
    }
  }, [corporateAccount, userId]);

  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    if (redirectPath) {
      window.location.href = redirectPath;
    }
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    console.log('searching roles...');
    const filtered = roles.filter(role =>
      role.level_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      role.role_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredRoles(filtered);
    setCurrentPage(1);
  };

  const handleEdit = (levelName, currentRoleName) => {
    if (permissions.canUpdate) {
      setEditingRows(prev => new Set([...prev, levelName]));
      setEditValues(prev => ({
        ...prev,
        [levelName]: currentRoleName
      }));
    }
  };

  const handleCancelEdit = (levelName) => {
    setEditingRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(levelName);
      return newSet;
    });
    setEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[levelName];
      return newValues;
    });
  };

  const handleSaveEdit = async (levelName) => {
    const newRoleName = editValues[levelName];
    if (!newRoleName || newRoleName.trim() === '') {
      setError('Role name cannot be empty');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Prepare the request body with only the changed level
      const requestBody = {
        corporate_account: corporateAccount,
        requesting_for_corporate_account: "",
        [levelName]: newRoleName.trim()
      };

      const response = await fetch(appConfig.getUrl('/update_account_access_levels_roles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'Success') {
        setError('Role updated successfully');

        // Update local state
        setRoles(prev => prev.map(role =>
          role.level_name === levelName
            ? { ...role, role_name: newRoleName.trim() }
            : role
        ));

        // Clear editing state
        handleCancelEdit(levelName);

        // Clear success message after a delay
        setTimeout(() => {
          setError(null);
        }, 3000);

      } else {
        throw new Error(data.status_description || 'Failed to update role');
      }
    } catch (err) {
      console.error('Error in handleSaveEdit:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (Object.keys(editValues).length === 0) {
      setError('No changes to save');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Prepare the request body with all current roles, updating changed ones
      const requestBody = {
        corporate_account: corporateAccount,
        requesting_for_corporate_account: ""
      };

      // Get current roles organized by level
      const rolesByLevel = {};
      roles.forEach(role => {
        rolesByLevel[role.level_name] = role.role_name;
      });

      // Update with edit values
      Object.keys(editValues).forEach(levelName => {
        const newRoleName = editValues[levelName];
        if (newRoleName && newRoleName.trim() !== '') {
          rolesByLevel[levelName] = newRoleName.trim();
        }
      });

      // Add all levels to request body
      for (let i = 1; i <= 9; i++) {
        const level = `level_${i}`;
        requestBody[level] = rolesByLevel[level] || '';
      }

      const response = await fetch(appConfig.getUrl('/update_account_access_levels_roles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'Success') {
        setError('All roles updated successfully');

        // Refresh roles from server
        await fetchRoles();

        // Clear editing state
        setEditingRows(new Set());
        setEditValues({});

        // Clear success message after a delay
        setTimeout(() => {
          setError(null);
        }, 3000);

      } else {
        throw new Error(data.status_description || 'Failed to update roles');
      }
    } catch (err) {
      console.error('Error in handleSaveAll:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (searchQuery === '' && corporateAccount && permissions.canViewAccessLevelDefinitions) {
      console.log('Search cleared, fetching all roles');
      fetchRoles();
    }
  }, [searchQuery, permissions.canViewAccessLevelDefinitions]);

  useEffect(() => {
    setFilteredRoles(roles);
  }, [roles]);

  useEffect(() => {
    const setInitialLoad = async () => {
      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }
      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          setCorporateAccount(result.userInfo.corporate_account);
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setAccountDescription(result.userInfo.account_description);
          console.log('User Info:', result.userInfo);
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);

  const fetchRoles = async () => {
    // Only fetch roles if user has view permission
    if (!permissions.canViewAccessLevelDefinitions) {
      console.log('User does not have permission to view access level definitions');
      return;
    }

    setLoading(true);
    setCurrentPage(1);

    try {
      const requestBody = {
        corporate_account: corporateAccount
      };

      const response = await fetch(appConfig.getUrl('/get_access_level_roles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('Fetched roles response is :', data);
      if (data.status === 'Success') {
        // Sort roles by level number for consistent display
        const sortedRoles = (data.access_level_roles || []).sort((a, b) => {
          const levelA = parseInt(a.level_name.replace('level_', ''));
          const levelB = parseInt(b.level_name.replace('level_', ''));
          return levelA - levelB;
        });
        setRoles(sortedRoles);
      } else {
        setRoles([]);
        if (data.access_level_roles?.length === 0) {
          setDialogMessage('No User Roles found to load.\nContact administrator to set up roles');
          setShowDialog(true);
        } else {
          throw new Error(data.status_description || 'Failed to fetch roles');
        }
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil((sortedRoles?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRoles = (sortedRoles || []).slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="flex h-[calc(100vh-64px)]">
        <div className="flex-1 overflow-y-auto p-6">
          <div style={styles.container}>

            {/* Title bar */}
            <h1 className="text-l font-bold text-blue-800 mt-6 mr-20 text-center">Manage User Roles</h1>

            {!permissions.canViewAccessLevelDefinitions ? (
              <div className="flex items-center justify-center h-full">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md mx-auto">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-yellow-800 mb-3 text-center">Access Restricted</h3>
                  <p className="text-yellow-700 text-center">
                    You do not have permission to view access level definitions. Please contact your administrator to request access.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center mb-4 mt-3 justify-between bg-blue-100 p-3 rounded">

                  {/* Search bar */}
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                      placeholder="Search roles..."
                      className="p-2 pr-10 border rounded w-full"
                    />
                    <button
                      onClick={handleSearch}
                      className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      <Search size={18} />
                    </button>
                  </div>

                  <h2 className="text-l font-bold text-blue-700 text-center flex-1">Account : {accountDescription} </h2>

                  <div className="flex items-center gap-2">
                    {editingRows.size > 0 && (
                      <div className="group relative">
                        <button
                          onClick={handleSaveAll}
                          disabled={!permissions.canUpdate || isSaving}
                          className={`flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 ${
                            !permissions.canUpdate || isSaving ? 'opacity-50' : ''
                          }`}
                        >
                          <Save size={18} className="mr-2" />
                          {isSaving ? 'Saving...' : `Save All (${editingRows.size})`}
                        </button>

                        {!permissions.canUpdate && (
                          <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                            Insufficient permission
                            <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* End Title bar */}

                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th
                        style={styles.th}
                        onClick={() => handleSort('level_name')}
                        className="select-none hover:bg-gray-200 transition-colors"
                      >
                        <div className="flex items-center">
                          <span style={{ textDecoration: 'underline' }}>Level</span>
                          <span className="ml-1">
                            {sortConfig.key === 'level_name' ? (
                              sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                            ) : null}
                          </span>
                        </div>
                      </th>
                      <th
                        style={styles.th}
                        onClick={() => handleSort('role_name')}
                        className="select-none hover:bg-gray-200 transition-colors"
                      >
                        <div className="flex items-center">
                          <span style={{ textDecoration: 'underline' }}>Role Name</span>
                          <span className="ml-1">
                            {sortConfig.key === 'role_name' ? (
                              sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                            ) : null}
                          </span>
                        </div>
                      </th>
                      <th style={{
                        backgroundColor: '#E5E4E2',
                        color: '#0064e5',
                        padding: '6px 12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #ddd',
                        fontSize: '0.875rem',
                        lineHeight: '1',
                        height: '32px'
                      }}>
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedRoles.map(role => (
                      <tr key={`${role.level_name}-${role.role_name}`}
                          className="border-t hover:bg-gray-50"
                      >
                        <td style={styles.td}>
                          {role.level_name.replace('level_', 'Level ')}
                        </td>
                        <td style={styles.td}>
                          {editingRows.has(role.level_name) ? (
                            <input
                              type="text"
                              value={editValues[role.level_name] || ''}
                              onChange={(e) => setEditValues(prev => ({
                                ...prev,
                                [role.level_name]: e.target.value
                              }))}
                              style={styles.editInput}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveEdit(role.level_name);
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit(role.level_name);
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            role.role_name
                          )}
                        </td>
                        <td style={styles.td}>
                          {editingRows.has(role.level_name) ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleSaveEdit(role.level_name)}
                                disabled={isSaving}
                                style={styles.saveButton}
                                title="Save changes"
                              >
                                <Check size={12} />
                              </button>
                              <button
                                onClick={() => handleCancelEdit(role.level_name)}
                                disabled={isSaving}
                                style={styles.cancelButton}
                                title="Cancel editing"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <div className="group relative">
                              <button
                                onClick={() => handleEdit(role.level_name, role.role_name)}
                                disabled={!permissions.canUpdate}
                                className={`p-1 rounded hover:bg-blue-100 ${
                                  !permissions.canUpdate ? 'opacity-50 ' : 'cursor-pointer'
                                }`}
                                style={{ color: permissions.canUpdate ? '#4CAF50' : '#999' }}
                                title="Edit role"
                              >
                                <Edit size={16} />
                              </button>

                              {/* Edit button tooltip */}
                              {!permissions.canUpdate && (
                                <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                                  Insufficient permission
                                  <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {sortedRoles.length === 0 && (
                      <tr>
                        <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                          No roles found. Adjust your search or contact administrator.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Error/Success Messages */}
                {error && (
                  <div className={`mb-4 p-2 rounded ${
                    error.toLowerCase().includes('successfully')
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-500'
                  }`}>
                    {error}
                  </div>
                )}

                {/* Pagination controls */}
                {!loading && sortedRoles.length > 0 && (
                  <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Total Entries: {sortedRoles.length}</span>
                      <span>| Showing {sortedRoles.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedRoles.length)} of {sortedRoles.length}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                      <button
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        aria-label="Next page"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1); // Reset to first page when changing items per page
                        }}
                        className="border rounded p-1 text-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                )}
                {/* End Pagination controls */}

                {/* Instructions */}
                {roles.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2">Instructions:</h3>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Click the edit icon to modify a role name inline</li>
                      <li>• Press Enter to save changes or Escape to cancel</li>
                      <li>• Use "Save All" to save multiple changes at once</li>
                      <li>• Role levels range from 1 (lowest) to 9 (highest)</li>
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={handleDialogClose}
          onCancel={handleDialogCancel}
        />
      )}
    </div>
  );
};

export default UserRolesDefinition;